import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { withViewport } from '@prisjakt/ui-viewport';
import ScrollHandler from './scroll-handler';

import { Tab } from './types';
import { AppState } from 'src/reducers';

import * as S from './styles';
import { getScrollPositionFromElement } from './utils';

type OwnProps = {
  defaultActiveTabId: string;
  tabs: Tab[];
  viewport: {
    top: number;
  };
};

const StickyTabNavigation = ({
  defaultActiveTabId,
  tabs,
  viewport,
}: OwnProps) => {
  const location = useLocation();
  const [_activeTab, _setActiveTab] = useState<string>(defaultActiveTabId);
  const [_scrollOngoing, _setScrollOngoing] = useState<boolean>(false);
  const activeTab = useRef(_activeTab);
  const scrollOngoing = useRef(_scrollOngoing);
  const isMobileSidebarOpen = useSelector(
    (state: AppState) => state.areas.navigation.mobileMenu.open
  );
  const showSearchBar = useSelector(
    (state: AppState) => state.areas.header.showSearchBar
  );
  const isScrolledDown = viewport && viewport.top > 90;
  const isFrontpage = location.pathname === '/';
  const isSearchPage = location.pathname.indexOf('/search') === 0;

  const setActiveTab = useCallback(data => {
    activeTab.current = data;
    _setActiveTab(data);
  }, []);

  const setScrollOngoing = useCallback(data => {
    scrollOngoing.current = data;
    _setScrollOngoing(data);
  }, []);

  const handleScrollOngoingState = useCallback(
    element => {
      if (
        activeTab.current === element.id &&
        window.scrollY >= element.position &&
        window.scrollY <= element.positionEnd
      )
        setScrollOngoing(false);
    },
    [setScrollOngoing]
  );

  const handleActiveStateOnClick = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      setScrollOngoing(true);
    },
    [setActiveTab, setScrollOngoing]
  );

  const handleActiveStateOnScroll = useCallback(() => {
    const tabElements = tabs
      .map(tab => {
        const element = document.getElementById(tab.id);
        const position = getScrollPositionFromElement(element);

        return {
          element,
          position,
          id: tab.id,
        };
      })
      .map((element, index, array) => {
        return {
          ...element,
          positionEnd: array[index + 1]
            ? array[index + 1].position - 1
            : document.body.scrollHeight,
        };
      });

    tabElements.map(element => {
      if (!element.position) return null;

      // If there's an ongoing tab click scrollTo, do not change tab active state.
      if (scrollOngoing.current) {
        return handleScrollOngoingState(element);
      }
      // Sets active tab based on scroll position for manual scroll.
      if (
        activeTab.current !== element.id &&
        window.scrollY >= element.position
      ) {
        return setActiveTab(element.id);
      }
      // Set active tab to default if scroll position is lower than defaultActiveTab position
      if (
        activeTab.current !== element.id &&
        element.id === defaultActiveTabId &&
        window.scrollY < element.position
      ) {
        return setActiveTab(defaultActiveTabId);
      }

      return null;
    });
  }, [setActiveTab, handleScrollOngoingState, tabs, defaultActiveTabId]);

  const allTabs = tabs.map(tab => ({
    ...tab,
    active: tab.id === activeTab.current,
    to: `${location.pathname}${location.search}${tab.id}`,
  }));

  return (
    <>
      <ScrollHandler onScrollCallback={handleActiveStateOnScroll} />
      <S.Tabs
        onClick={handleActiveStateOnClick}
        tabs={allTabs}
        mobileSearchBarVisible={
          (showSearchBar || isSearchPage || (isFrontpage && !isScrolledDown)) &&
          !isMobileSidebarOpen
        }
      />
    </>
  );
};

export default withViewport(StickyTabNavigation);
